"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  Button,
  TextInput,
  Modal,
  Spinner,
  Badge,
} from "flowbite-react";
import { HiPlus, HiTrash } from "react-icons/hi";
import { getJournalistURL } from "../utils/url";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment";

const GetJournalists = () => {
  const [names, setNames] = useState([]);
  const [newJournalistName, setNewJournalistName] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const { user } = useAuth0();
  const { email } = user;
  const [articles, setArticles] = useState([]);
  const [isArticlesLoading, setIsArticlesLoading] = useState(false);
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [selectedName, setSelectedName] = useState();

  // Fetch journalists when the page loads
  const fetchNames = async () => {
    try {
      setIsLoadingNames(true);
      const response = await axios.get(
        getJournalistURL("api/v1/muckrack/names")
      );
      const names = response.data.data.names;
      setNames(response.data.data.names);
    } catch (error) {
      console.error("Error fetching journalists:", error);
    } finally {
      setIsLoadingNames(false);
    }
  };

  // Fetch articles for each journalist
  const createTasks = async () => {
    setIsCreatingTasks(true);
    try {
      const response = await axios.post(
        getJournalistURL("api/v1/muckrack/create-tasks")
      );
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setIsCreatingTasks(false);
    }
  };

  // Add a new journalist
  const handleAddJournalist = async () => {
    if (!newJournalistName.trim()) return;

    try {
      const { data } = await axios.post(
        getJournalistURL("api/v1/muckrack/names"),
        {
          names: [newJournalistName.trim()],
          email,
        }
      );
      setNames((prev) => [
        ...prev,
        ...(data?.data?.names?.map((item) => ({ ...item, articles: [] })) ??
          []),
      ]);
      setNewJournalistName("");
    } catch (error) {
      console.error("Error adding journalist:", error);
    }
  };

  // Remove a journalist locally
  const handleRemoveJournalist = async (name) => {
    const searchParams = new URLSearchParams();
    searchParams.set("name", name);
    try {
      await axios.delete(
        getJournalistURL(`api/v1/muckrack/names?${searchParams.toString()}`)
      );
    } catch (error) {
      console.error("Error removing journalist:", error);
    }
    setNames(names.filter((journalist) => journalist.keywordName !== name));
  };

  useEffect(() => {
    fetchNames();
  }, []);

  const openJournalistModal = async (nameId) => {
    if (isArticlesLoading) {
      alert("Please wait for the articles to load");
      return;
    }
    setIsArticlesLoading(true);
    setSelectedName(names.find((name) => name.id === nameId));
    try {
      const { data: response } = await axios.get(
        getJournalistURL(`api/v1/muckrack/names/${nameId}/articles`)
      );
      setArticles([...response.data.articles]);
      setOpenModal(true);
    } catch (error) {
      console.error("Error fetching journalist:", error);
      setArticles([]);
    } finally {
      setIsArticlesLoading(false);
    }
  };

  return (
    <div className="p-5">
      <div className="mb-4">
        <p className="mb-4 text-gray-700">
          Add a list of journalists you want to follow. Then receive an email of
          their most recently published article titles over the last 7 days.
        </p>

        {isLoadingNames ? (
          <div className="w-full flex justify-center">
            <Spinner />
          </div>
        ) : (
          <Table className="bg-white border-b border-l border-r border-gray-200 rounded-t-md shadow-md">
            <Table.Head>
              <Table.HeadCell className="text-gray-900">Name</Table.HeadCell>
              <Table.HeadCell className="text-gray-900">Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {names.map((name, index) => (
                <Table.Row
                  key={index}
                  className="hover:bg-gray-100 transition-colors"
                >
                  <Table.Cell
                    className="text-gray-800 font-medium"
                    onClick={() => openJournalistModal(name.id)}
                  >
                    <div className="flex space-x-2 cursor-pointer items-center">
                      <span>{name.keywordName}</span>
                      <Badge color="info" size="sm">
                        {name.countArticles}
                      </Badge>
                      {selectedName?.id === name.id &&
                      (isArticlesLoading || isCreatingTasks) ? (
                        <Spinner />
                      ) : (
                        ""
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      color="light"
                      className="border-none flex items-center gap-2 text-red-600 hover:text-red-700 p-0"
                      onClick={() => handleRemoveJournalist(name.keywordName)}
                    >
                      <HiTrash className="w-5 h-5" />
                      Remove
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
              {/* Add Journalist Row */}
              <Table.Row className="hover:bg-gray-50">
                <Table.Cell>
                  <TextInput
                    type="text"
                    value={newJournalistName}
                    onChange={(e) => setNewJournalistName(e.target.value)}
                    placeholder="Enter journalist name"
                    className="w-full"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size="sm"
                    color="light"
                    className="border-none flex items-center gap-2 text-green-600 hover:text-green-700 p-0"
                    onClick={handleAddJournalist}
                  >
                    <HiPlus className="w-5 h-5" />
                    Add
                  </Button>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        )}
      </div>

      <Button
        onClick={createTasks}
        disabled={names.length === 0 || isCreatingTasks}
        className={`w-full py-2 text-white rounded-lg ${
          isCreatingTasks || names.length === 0
            ? "bg-gray-300 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
      >
        {isCreatingTasks ? "Fetching Articles..." : "Fetch Articles"}
      </Button>
      <Modal
        dismissible
        show={openModal}
        onClose={() => setOpenModal(false)}
        size="4xl"
      >
        <Modal.Header>Articles</Modal.Header>
        <Modal.Body>
          <Table className="bg-white border-b border-l border-r border-gray-200 rounded-t-md shadow-md">
            <Table.Head>
              <Table.HeadCell className="text-gray-900">Title</Table.HeadCell>
              <Table.HeadCell className="text-gray-900">
                Created At
              </Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {articles
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((article, index) => (
                  <Table.Row
                    key={index}
                    className="hover:bg-gray-100 transition-colors"
                  >
                    <Table.Cell>{article.title}</Table.Cell>
                    <Table.Cell>
                      {moment(article.createdAt).format("YYYY-MM-DD HH:mm")}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setOpenModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GetJournalists;
